if ( typeof QuoteRequest !== 'function' ) {

	/*
	 * "Request a quote" popup for corporate gift products (snippets/quote-request.liquid).
	 * The optional logo is uploaded straight from the browser to Cloudinary (unsigned preset),
	 * its link is written into a hidden field and then the Shopify contact form is submitted
	 * natively, so Shopify's spam protection and success/error handling keep working.
	 */

	const QUOTE_ALLOWED_EXTENSIONS = ['png', 'jpg', 'jpeg', 'webp', 'svg', 'pdf', 'ai', 'eps'];
	const QUOTE_RASTER_FORMATS = ['png', 'jpg', 'jpeg', 'webp', 'gif'];

	class QuoteRequest extends HTMLElement {

		connectedCallback() {

			if ( this._ready ) return;
			this._ready = true;

			this.dialog = this.querySelector('dialog');
			this.form = this.dialog.querySelector('form');
			this.content = this.form.querySelector('[data-quote-content]');
			this.successPanel = this.form.querySelector('[data-quote-state="success"]');
			this.errorBox = this.form.querySelector('[data-quote-error]');
			this.submitButton = this.form.querySelector('[data-quote-submit]');
			this.submitText = this.form.querySelector('[data-quote-submit-text]');
			this.submitLabel = this.submitText.textContent;
			this.status = this.form.querySelector('[data-quote-status]');
			this.storageKey = `quote-request:${this.dataset.productHandle}`;

			this.upload = this.form.querySelector('[data-quote-upload]');
			if ( this.upload ) {
				this.fileInput = this.upload.querySelector('[data-quote-file]');
				this.dropZone = this.upload.querySelector('[data-quote-drop]');
				this.fileInfo = this.upload.querySelector('[data-quote-file-info]');
				this.fileThumb = this.upload.querySelector('[data-quote-file-thumb]');
				this.progress = this.upload.querySelector('[data-quote-progress]');
				this.logoUrlInput = this.form.querySelector('[data-quote-logo-url]');
				this.logoPreviewInput = this.form.querySelector('[data-quote-logo-preview]');
				this.logoNoneValue = this.logoUrlInput.value;
				this.maxBytes = ( parseFloat(this.dataset.maxMb) || 10 ) * 1024 * 1024;
				this.initUpload();
			}

			this.querySelector('[data-quote-open]').addEventListener('click', () => this.open());
			// close on backdrop click (only if the press started there too, so selecting text never closes it)
			this.dialog.addEventListener('pointerdown', e => this.pressedBackdrop = e.target === this.dialog);
			this.dialog.addEventListener('click', e => {
				if ( ( e.target === this.dialog && this.pressedBackdrop ) || e.target.closest('[data-quote-close]') ) this.close();
			});
			this.dialog.addEventListener('close', () => this.onClose());
			this.form.addEventListener('submit', this.onSubmit.bind(this));

			// reset the button if the visitor comes back with the browser's back button
			window.addEventListener('pageshow', e => {
				if ( e.persisted ) {
					this.cancelHandOff();
					this.setSubmitting(false);
				}
			});

			// earliest delivery date is today
			const dateInput = this.form.querySelector('[data-quote-date]');
			if ( dateInput ) {
				const now = new Date();
				dateInput.min = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().split('T')[0];
			}

			// after a submit Shopify reloads the page with ?contact_posted=true (or with errors)
			if ( this.successPanel ) {
				this.clearDraft();
				this.open('success');
				this.cleanUrl();
			} else if ( this.form.querySelector('[data-quote-state="error"]') ) {
				this.restoreDraft();
				this.open();
				this.cleanUrl();
			}

			// theme editor: preview the popup while the block is selected
			if ( window.Shopify && Shopify.designMode ) {
				document.addEventListener('shopify:block:select', e => { if ( e.target === this ) this.open(); });
				document.addEventListener('shopify:block:deselect', e => { if ( e.target === this ) this.close(); });
			}

		}

		open(mode = 'form') {
			this.cancelHandOff();
			const showSuccess = mode === 'success' && this.successPanel;
			if ( this.successPanel ) this.successPanel.hidden = ! showSuccess;
			this.content.hidden = showSuccess;
			if ( ! this.dialog.open ) {
				this.dialog.showModal();
				document.documentElement.classList.add('quote-dialog-open');
			}
			this.dialog.querySelector('.quote-dialog__body').scrollTop = 0;
		}

		close() {
			if ( this.dialog.open ) this.dialog.close();
		}

		onClose() {
			// the close event is async, the popup may already be open again
			if ( this.dialog.open ) return;
			document.documentElement.classList.remove('quote-dialog-open');
			// next time the button is used, show the form again
			if ( this.successPanel ) {
				this.successPanel.remove();
				this.successPanel = null;
				this.content.hidden = false;
			}
		}

		async onSubmit(e) {

			// the browser already validated the required fields at this point.
			// Stop Shopify's captcha listener too: it would send the form before the logo upload is done.
			// We send it ourselves below through form.submit(), which the captcha wraps.
			e.preventDefault();
			e.stopImmediatePropagation();
			if ( this.submitting ) return;
			this.showError('');

			if ( this.upload && this.upload.hasAttribute('data-required') && ! this.file ) {
				this.showError(this.dataset.textRequired);
				this.dropZone.focus();
				return;
			}

			this.setSubmitting(true);

			if ( this.file && ! this.uploadedFile ) {
				try {
					this.uploadedFile = await this.uploadLogo(this.file);
				} catch ( error ) {
					console.error('Quote request: logo upload failed', error);
					this.setSubmitting(false);
					this.showError(this.dataset.textUploadFailed);
					return;
				}
			}

			if ( this.upload ) this.applyLogoFields();

			this.submitText.textContent = this.dataset.textSending;
			this.status.textContent = this.dataset.textSending;

			if ( ! await this.waitForCaptcha() ) {
				this.setSubmitting(false);
				this.showError(this.dataset.textSendFailed);
				return;
			}

			this.saveDraft();
			this.handOff();
			// Shopify's spam protection replaces form.submit() with a version that adds the captcha token first
			typeof this.form.submit === 'function' ? this.form.submit() : HTMLFormElement.prototype.submit.call(this.form);

		}

		/* Shopify spam protection (hCaptcha / reCAPTCHA) */

		waitForCaptcha() {

			// Shopify binds the captcha to a contact form on its first focus, make sure that happened
			this.form.dispatchEvent(new FocusEvent('focusin', { bubbles: true }));
			const isBound = () => this.form.hasAttribute('data-hcaptcha-bound') || this.form.hasAttribute('data-recaptcha-bound');

			// no spam protection on this form, or it's ready
			if ( this.form.dataset.cptcha !== 'true' || isBound() ) return Promise.resolve(true);

			return new Promise(resolve => {
				const started = Date.now();
				const check = () => {
					if ( isBound() ) resolve(true);
					else if ( Date.now() - started > 10000 ) resolve(false);
					else setTimeout(check, 100);
				};
				check();
			});

		}

		// If the captcha needs a visible challenge, it's shown on the page, which the modal popup would block.
		// So close the popup while sending and show the progress on the page button instead.
		handOff() {
			const openButton = this.querySelector('[data-quote-open]');
			const openText = openButton.querySelector('.button__text');
			this.openLabel = this.openLabel || openText.textContent;
			openText.textContent = this.dataset.textSending;
			openButton.disabled = true;
			this.close();
			// the visitor closed the challenge without solving it: let them try again
			this.handOffTimer = setTimeout(() => this.cancelHandOff(), 15000);
		}

		cancelHandOff() {
			clearTimeout(this.handOffTimer);
			if ( ! this.openLabel ) return;
			const openButton = this.querySelector('[data-quote-open]');
			openButton.querySelector('.button__text').textContent = this.openLabel;
			openButton.disabled = false;
			this.openLabel = null;
			this.setSubmitting(false);
		}

		setSubmitting(state) {
			this.submitting = state;
			this.submitButton.classList.toggle('working', state);
			this.submitButton.disabled = state;
			if ( ! state ) {
				this.submitText.textContent = this.submitLabel;
				this.status.textContent = '';
				this.setProgress(null);
			}
		}

		showError(message) {
			this.errorBox.textContent = message;
			this.errorBox.hidden = ! message;
			if ( message ) this.errorBox.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
		}

		/* Logo upload */

		initUpload() {

			this.fileInput.addEventListener('change', () => {
				if ( this.fileInput.files.length ) this.setFile(this.fileInput.files[0]);
			});

			this.upload.querySelector('[data-quote-file-remove]').addEventListener('click', () => {
				this.setFile(null);
				this.dropZone.focus();
			});

			// keyboard users can open the file picker from the label
			this.dropZone.tabIndex = 0;
			this.dropZone.addEventListener('keydown', e => {
				if ( e.key === 'Enter' || e.key === ' ' ) {
					e.preventDefault();
					this.fileInput.click();
				}
			});

			['dragenter', 'dragover'].forEach(type => this.dropZone.addEventListener(type, e => {
				e.preventDefault();
				this.dropZone.classList.add('is-dragging');
			}));
			['dragleave', 'drop'].forEach(type => this.dropZone.addEventListener(type, e => {
				e.preventDefault();
				this.dropZone.classList.remove('is-dragging');
			}));
			this.dropZone.addEventListener('drop', e => {
				if ( e.dataTransfer && e.dataTransfer.files.length ) this.setFile(e.dataTransfer.files[0]);
			});

		}

		setFile(file) {

			this.showError('');

			if ( file ) {
				const extension = file.name.split('.').pop().toLowerCase();
				if ( ! QUOTE_ALLOWED_EXTENSIONS.includes(extension) ) {
					this.showError(this.dataset.textInvalidType);
					file = null;
				} else if ( file.size > this.maxBytes ) {
					this.showError(this.dataset.textTooLarge);
					file = null;
				}
			}

			this.file = file;
			this.uploadedFile = null;
			if ( ! file ) this.fileInput.value = '';

			if ( this.thumbUrl ) {
				URL.revokeObjectURL(this.thumbUrl);
				this.thumbUrl = null;
			}

			this.fileInfo.hidden = ! file;
			this.dropZone.hidden = !! file;

			if ( file ) {
				this.upload.querySelector('[data-quote-file-name]').textContent = file.name;
				this.upload.querySelector('[data-quote-file-size]').textContent = this.formatBytes(file.size);
				const isImage = /^image\/(png|jpe?g|webp|svg\+xml)$/.test(file.type);
				if ( isImage ) {
					this.thumbUrl = URL.createObjectURL(file);
					this.fileThumb.src = this.thumbUrl;
				}
				this.fileThumb.hidden = ! isImage;
			}

		}

		uploadLogo(file) {

			return new Promise((resolve, reject) => {

				const clean = value => ( value || '' ).replace(/[|=]/g, ' ').trim().slice(0, 200);
				const company = this.form.querySelector('[data-quote-company]');

				const data = new FormData();
				data.append('file', file);
				data.append('upload_preset', this.dataset.uploadPreset);
				data.append('tags', ['quote-request', this.dataset.productHandle].join(','));
				data.append('context', `product=${clean(this.dataset.productTitle)}|company=${clean(company && company.value)}`);

				const xhr = new XMLHttpRequest();
				xhr.open('POST', `https://api.cloudinary.com/v1_1/${encodeURIComponent(this.dataset.cloudName)}/auto/upload`);
				xhr.timeout = 120000;

				xhr.upload.addEventListener('progress', e => {
					if ( e.lengthComputable ) this.setProgress(e.loaded / e.total);
				});

				xhr.addEventListener('load', () => {
					let response = {};
					try { response = JSON.parse(xhr.responseText); } catch ( error ) {}
					if ( xhr.status >= 200 && xhr.status < 300 && response.secure_url ) {
						resolve(response);
					} else {
						reject(new Error(( response.error && response.error.message ) || `HTTP ${xhr.status}`));
					}
				});
				xhr.addEventListener('error', () => reject(new Error('Network error')));
				xhr.addEventListener('timeout', () => reject(new Error('Timeout')));

				this.setProgress(0);
				xhr.send(data);

			});

		}

		setProgress(value) {
			if ( ! this.progress ) return;
			this.progress.hidden = value === null;
			if ( value === null ) return;
			const percent = Math.round(value * 100);
			this.progress.firstElementChild.style.width = `${percent}%`;
			this.submitText.textContent = `${this.dataset.textUploading} ${percent}%`;
			this.status.textContent = this.dataset.textUploading;
		}

		applyLogoFields() {

			const result = this.uploadedFile;
			this.logoUrlInput.value = result ? result.secure_url : this.logoNoneValue;

			// vector files and PDFs can't be viewed in every mail client, so add a PNG preview link
			let previewUrl = '';
			if ( result && result.resource_type === 'image' && ! QUOTE_RASTER_FORMATS.includes(result.format) ) {
				previewUrl = `https://res.cloudinary.com/${this.dataset.cloudName}/image/upload/f_png,w_1200,c_limit/v${result.version}/${result.public_id}.png`;
			}
			this.logoPreviewInput.value = previewUrl;
			this.logoPreviewInput.disabled = ! previewUrl;

		}

		formatBytes(bytes) {
			return bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${( bytes / 1024 / 1024 ).toFixed(1)} MB`;
		}

		/* Keep the typed values if Shopify sends the form back with an error */

		saveDraft() {
			const values = {};
			this.form.querySelectorAll('[data-quote-persist]').forEach(field => values[field.id] = field.value);
			try { sessionStorage.setItem(this.storageKey, JSON.stringify(values)); } catch ( error ) {}
		}

		restoreDraft() {
			let values = null;
			try { values = JSON.parse(sessionStorage.getItem(this.storageKey)); } catch ( error ) {}
			if ( ! values ) return;
			this.form.querySelectorAll('[data-quote-persist]').forEach(field => {
				if ( values[field.id] && ! field.value ) field.value = values[field.id];
			});
		}

		clearDraft() {
			try { sessionStorage.removeItem(this.storageKey); } catch ( error ) {}
		}

		cleanUrl() {
			const url = new URL(window.location.href);
			url.searchParams.delete('contact_posted');
			url.hash = '';
			window.history.replaceState(window.history.state, '', url.toString());
		}

	}

	window.QuoteRequest = QuoteRequest;

	if ( typeof customElements.get('quote-request') == 'undefined' ) {
		customElements.define('quote-request', QuoteRequest);
	}

}
